import React, { Fragment } from 'react';
import {
    DateField,
    TextField,
    Filter,
    TextInput,
    SelectInput,
    DateInput,
    ShowButton,
    FunctionField,
    BulkExportButton,
    BulkDeleteButton,
} from 'react-admin';
import UserReferenceField from '../components/UserReferenceField';
import Revision from './Revision';
import BoxitPagination from '../components/BoxitPagination';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

const RevisionFilter = (props: any) => (
    <Filter variant="outlined" {...props}>
        <SelectInput
            source="model"
            choices={[
                { id: 'box', name: 'Box' },
                { id: 'storage', name: 'Storage' },
                { id: 'request', name: 'Request' },
                { id: 'job', name: 'Job' },
            ]}
            alwaysOn
        />
        <DateInput source="createdAt[$gte]" label="Created From" />
        <DateInput source="createdAt[$lte]" label="Created To" />
    </Filter>
);

// BulkActionButtons component for the RevisionsList
const BulkActionButtons = props => (
    <Fragment>
        <BulkExportButton {...props} />
        <BulkDeleteButton undoable={false} {...props} />
    </Fragment>
);

// Custom field components
const DocumentField = ({ record }) => {
    if (!record.documentId || !record.model) return null;

    // Create clickable link based on model type
    const getModelRoute = (model: string) => {
        switch (model) {
            case 'box':
                return 'boxes';
            case 'storage':
                return 'storages';
            case 'request':
                return 'requests';
            case 'job':
                return 'jobs';
            case 'user':
                return 'users';
            default:
                return model + 's'; // fallback pluralization
        }
    };

    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                const route = getModelRoute(record.model);
                window.location.href = `/#/${route}/${record.documentId}/show`;
            }}
        >
            {record.documentId}
        </button>
    );
};

const UserField = ({ record }) => {
    if (!record.userId) return null;

    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/users/${record.userId}/show`;
            }}
        >
            {record.userId}
        </button>
    );
};

const PartnerField = ({ record }) => {
    if (!record.partnerId) return null;

    return (
        <button
            style={{
                cursor: 'pointer',
                color: '#1890ff',
                outline: 'none',
                background: 'none',
                border: 'none',
            }}
            onClick={() => {
                window.location.href = `/#/partners/${record.partnerId}/show`;
            }}
        >
            {record.partnerId}
        </button>
    );
};

const RevisionChangesField = ({ record }) => {
    if (!record.revisionchanges || record.revisionchanges.length === 0) {
        return <span>No changes</span>;
    }

    return (
        <div>
            {record.revisionchanges.map((change, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                    <strong>{change.path}:</strong>{' '}
                    {change.document
                        ? JSON.stringify(JSON.parse(change.document), null, 2)
                        : 'No data'}
                </div>
            ))}
        </div>
    );
};

const RevisionsList = (props: any) => {
    // Define default columns (visible by default) - matching original RevisionsList
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 1 },
        { id: 'createdAt', label: 'Date', visible: true, order: 2 },
        { id: 'userId', label: 'User', visible: true, order: 3 },
        { id: 'model', label: 'Model', visible: true, order: 4 },
        { id: 'revision', label: 'Revision', visible: true, order: 5 },
    ];

    // Define all available columns (including optional ones)
    const allColumns: ColumnConfig[] = [
        ...defaultColumns,
        // Optional columns (hidden by default)
        { id: 'id', label: 'ID', visible: false, order: 6 },
        { id: 'documentId', label: 'Document ID', visible: false, order: 7 },
        { id: 'document', label: 'Document', visible: false, order: 8 },
        { id: 'updatedAt', label: 'Updated At', visible: false, order: 9 },
        { id: 'partnerId', label: 'Partner ID', visible: false, order: 10 },
        { id: 'partnerLink', label: 'Partner', visible: false, order: 11 },
        { id: 'userLink', label: 'User Link', visible: false, order: 12 },
        {
            id: 'documentLink',
            label: 'Document Link',
            visible: false,
            order: 13,
        },
        {
            id: 'revisionChanges',
            label: 'Revision Changes',
            visible: false,
            order: 14,
        },
        { id: 'show', label: 'Actions', visible: false, order: 15 },
    ];

    const renderColumn = (columnId: string) => {
        console.log('🔍 REVISIONS DEBUG: Rendering column:', columnId);
        switch (columnId) {
            case 'serial':
                console.log('🔍 REVISIONS DEBUG: Rendering serial column');
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        label="Date"
                        source="createdAt"
                        showTime
                    />
                );
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="updatedAt"
                        showTime
                    />
                );
            case 'userId':
                return (
                    <UserReferenceField
                        key={columnId}
                        label="User"
                        link={false}
                    />
                );
            case 'userLink':
                return (
                    <FunctionField
                        key={columnId}
                        label="User Link"
                        render={record => <UserField record={record} />}
                    />
                );
            case 'model':
                return (
                    <TextField key={columnId} source="model" sortable={false} />
                );
            case 'documentId':
                return <TextField key={columnId} source="documentId" />;
            case 'documentLink':
                return (
                    <FunctionField
                        key={columnId}
                        label="Document Link"
                        render={record => <DocumentField record={record} />}
                    />
                );
            case 'document':
                return (
                    <FunctionField
                        key={columnId}
                        label="Document"
                        render={record =>
                            record.document ? (
                                <pre
                                    style={{
                                        fontSize: '12px',
                                        maxWidth: '200px',
                                        overflow: 'auto',
                                    }}
                                >
                                    {JSON.stringify(
                                        JSON.parse(record.document),
                                        null,
                                        2
                                    )}
                                </pre>
                            ) : (
                                '-'
                            )
                        }
                    />
                );
            case 'revision':
                return <Revision key={columnId} />;
            case 'partnerId':
                return <TextField key={columnId} source="partnerId" />;
            case 'partnerLink':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'revisionChanges':
                return (
                    <FunctionField
                        key={columnId}
                        label="Changes"
                        render={record => (
                            <RevisionChangesField record={record} />
                        )}
                    />
                );
            case 'show':
                return <ShowButton key={columnId} />;
            default:
                return <TextField key={columnId} source={columnId} />;
        }
    };

    const combinedProps = {
        title: 'Revisions',
        sort: { field: 'createdAt', order: 'DESC' },
        perPage: 10,
        pagination: <BoxitPagination />,
        filters: <RevisionFilter />,
        defaultColumns: allColumns,
        storageKey: 'revisionsList',
        renderColumn,
        filtersComponent: <RevisionFilter />,
        bulkActionButtons: <BulkActionButtons />,
        permissions: [], // Add empty permissions array
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'revisions',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    console.log('🔍 REVISIONS DEBUG: Combined props:', combinedProps);
    console.log('🔍 REVISIONS DEBUG: Default columns:', allColumns);

    return <CustomizableGrid {...combinedProps} />;
};

export default RevisionsList;
